import { extent } from 'd3-array';
import { interpolateLab } from 'd3-interpolate';
import { scaleLinear } from 'd3-scale';

import type { ColorScheme, ColorSchemeDirection } from './metric-scales';

const NegativeMaxColor = '#B34B6C';

const NoDataFillColor = 'transparent';

export type MapThemeName = 'light' | 'dark';

export type MapThemeColors = {
  border: string;
  highlight: string;
  neutral: string;
  noDataFill: string;
  positiveMax: string;
  negativeMax: string;
  positivePalette: readonly string[];
  negativePalette: readonly string[];
};

function buildSequentialPalette(from: string, to: string): string[] {
  const interpolate = interpolateLab(from, to);
  return [0.2, 0.4, 0.6, 0.8, 1].map((t) => interpolate(t));
}

const LIGHT_MAP_COLORS: MapThemeColors = {
  border: '#C8C8CA',
  highlight: '#63A2D9',
  neutral: '#FBFBFB',
  noDataFill: NoDataFillColor,
  positiveMax: '#63A2D9',
  negativeMax: NegativeMaxColor,
  positivePalette: buildSequentialPalette('#FBFBFB', '#63A2D9'),
  negativePalette: buildSequentialPalette('#FBFBFB', NegativeMaxColor),
};

const DARK_MAP_COLORS: MapThemeColors = {
  border: '#25282D',
  highlight: '#A9CCEA',
  neutral: '#43474B',
  noDataFill: NoDataFillColor,
  positiveMax: '#A9CCEA',
  negativeMax: NegativeMaxColor,
  positivePalette: buildSequentialPalette('#43474B', '#A9CCEA'),
  negativePalette: buildSequentialPalette('#43474B', NegativeMaxColor),
};

export function getMapThemeColors(theme: MapThemeName): MapThemeColors {
  return theme === 'dark' ? DARK_MAP_COLORS : LIGHT_MAP_COLORS;
}

export type ScaleInfo = {
  range: string[];
  domain: [number, number];
};

export function getColorScaleInfo(
  values: number[],
  type: ColorScheme,
  direction: ColorSchemeDirection,
  colors: MapThemeColors,
  domain?: { min?: number; max?: number; center?: number }
): { color: (value: number) => string; info: ScaleInfo } {
  if (!values.length) {
    let palette: string[];
    if (type === 'diverging') {
      if (direction === 'negative') {
        palette = [
          ...[...colors.positivePalette].reverse(),
          colors.neutral,
          ...colors.negativePalette,
        ] as string[];
      } else {
        palette = [
          ...[...colors.negativePalette].reverse(),
          colors.neutral,
          ...colors.positivePalette,
        ] as string[];
      }
    } else {
      palette = (
        direction === 'negative'
          ? [...colors.negativePalette]
          : [...colors.positivePalette]
      ) as string[];
    }
    return {
      color: () => colors.noDataFill,
      info: {
        range: palette,
        domain: [0, 0],
      },
    };
  }

  const [dataMin, dataMax] = extent(values) as [number, number];

  if (type === 'diverging') {
    const actualCenter = domain?.center ?? 0;
    let minVal = domain?.min ?? dataMin;
    let maxVal = domain?.max ?? dataMax;

    const maxDeviation = Math.max(
      Math.abs(minVal - actualCenter),
      Math.abs(maxVal - actualCenter)
    );
    minVal = actualCenter - maxDeviation;
    maxVal = actualCenter + maxDeviation;

    const startColor =
      direction === 'negative' ? colors.positiveMax : colors.negativeMax;
    const neutral = colors.neutral;
    const endColor =
      direction === 'negative' ? colors.negativeMax : colors.positiveMax;

    const toT = scaleLinear<number>()
      .domain([minVal, maxVal])
      .range([0, 1])
      .clamp(true);

    const interpolateLower = interpolateLab(startColor, neutral);
    const interpolateUpper = interpolateLab(neutral, endColor);

    return {
      color: (v: number) => {
        if (!Number.isFinite(v)) return colors.noDataFill;
        const t = toT(v);
        if (t <= 0.5) {
          return interpolateLower(t * 2) ?? colors.noDataFill;
        }
        return interpolateUpper((t - 0.5) * 2) ?? colors.noDataFill;
      },
      info: {
        range: [startColor, neutral, endColor],
        domain: [minVal, maxVal],
      },
    };
  }

  const minVal = domain?.min ?? Math.min(0, dataMin);
  const maxVal = domain?.max ?? dataMax;

  const palette: string[] =
    direction === 'negative'
      ? ([...colors.negativePalette] as string[])
      : ([...colors.positivePalette] as string[]);

  if (minVal === maxVal) {
    const singleColor =
      direction === 'negative'
        ? (colors.negativePalette[0] ?? colors.noDataFill)
        : (colors.positivePalette[0] ?? colors.noDataFill);
    return {
      color: () => singleColor,
      info: {
        range: palette,
        domain: [minVal, maxVal],
      },
    };
  }

  const startColor = colors.neutral;
  const endColor =
    direction === 'negative' ? colors.negativeMax : colors.positiveMax;

  const toT = scaleLinear<number>()
    .domain([minVal, maxVal])
    .range([0, 1])
    .clamp(true);

  const interpolate = interpolateLab(startColor, endColor);

  return {
    color: (v: number) => {
      if (!Number.isFinite(v)) return colors.noDataFill;
      const t = toT(v);
      return interpolate(t) ?? colors.noDataFill;
    },
    info: {
      range: [startColor, endColor],
      domain: [minVal, maxVal],
    },
  };
}
